import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { triggerDownload } from '@/utils';
import exportRequestService from '@/services/security/exportRequestService';
import { pendingApprovalMessage } from '@/utils/exportResponse';
import { formatWhen } from '@/utils/formatWhen';
import { downloadName, sortRequests, statusOf } from './exportRequestStatus';

/**
 * Export Requests (OL-154). When the firm turns on "Exports require approval",
 * staff exports land here. Approvers approve or reject them; the person who
 * asked downloads an approved export once, within 24 hours.
 */
const ExportRequests = () => {
  const [rows, setRows] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // { id, note }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await exportRequestService.list();
      setRows(sortRequests(data?.requests));
      setCanApprove(!!data?.can_approve);
    } catch (error) {
      toast.error('Could not load export requests.', { description: error?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, action, note = '') => {
    setBusyId(id);
    try {
      const { message } = await exportRequestService[action](id, note);
      toast.success(message);
      setRejecting(null);
      await load();
    } catch (error) {
      toast.error(error?.message || 'That did not work. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const download = async (row) => {
    setBusyId(row.id);
    try {
      const blob = await exportRequestService.download(row.replay_path, row.id);
      if (await pendingApprovalMessage(blob)) {
        toast.error('This export is not approved yet.');
        return;
      }
      triggerDownload(blob, downloadName(row));
      toast.success('Export downloaded.');
      await load();
    } catch {
      toast.error('Could not download this export.', {
        description: 'It may have expired or already been downloaded. Ask for it again from the report or list.',
      });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const actionsFor = (row) => {
    const busy = busyId === row.id;

    if (row.downloadable) {
      return (
        <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => download(row)}>
          <KeenIcon icon="file-down" /> {busy ? 'Downloading...' : 'Download'}
        </button>
      );
    }

    if (canApprove && row.status === 'pending' && !row.is_mine) {
      if (rejecting?.id === row.id) {
        return (
          <div className="flex flex-col gap-2 min-w-56">
            <textarea
              className="textarea text-sm"
              rows={2}
              placeholder="Reason (optional, sent to the requester)"
              value={rejecting.note}
              onChange={(e) => setRejecting({ id: row.id, note: e.target.value })}
            />
            <div className="flex gap-2 justify-end">
              <button className="btn btn-sm btn-light" disabled={busy} onClick={() => setRejecting(null)}>Cancel</button>
              <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => review(row.id, 'reject', rejecting.note)}>
                Reject
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="flex gap-2 justify-end">
          <button className="btn btn-sm btn-success" disabled={busy} onClick={() => review(row.id, 'approve')}>
            Approve
          </button>
          <button className="btn btn-sm btn-light" disabled={busy} onClick={() => setRejecting({ id: row.id, note: '' })}>
            Reject
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <Container>
      <div className="card min-w-full mb-4">
        <div className="card-header">
          <div>
            <h3 className="card-title">Export Requests</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-[640px]">
              {canApprove
                ? 'Staff exports waiting for approval, and past decisions. An approved export can be downloaded once, by the person who asked, within 24 hours.'
                : 'Exports you asked for. You will be notified when one is approved; download it here within 24 hours.'}
            </p>
          </div>
          <button className="btn btn-sm btn-light" onClick={load} disabled={loading}>
            <KeenIcon icon="arrows-circle" /> Refresh
          </button>
        </div>

        <div className="card-table scrollable-x-auto">
          <table className="table align-middle text-sm text-gray-700">
            <thead>
              <tr>
                <th className="text-start">Export</th>
                <th className="text-start">Requested by</th>
                <th className="text-start">Requested</th>
                <th className="text-start">Status</th>
                <th className="text-start">Reviewed</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-6">Loading...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-6">No export requests yet.</td></tr>
              )}
              {!loading && rows.map((row) => {
                const status = statusOf(row.status);
                return (
                  <tr key={row.id}>
                    <td className="font-medium text-gray-900">{row.label}</td>
                    <td>{row.requested_by || '-'}</td>
                    <td>{formatWhen(row.requested_at)}</td>
                    <td>
                      <span className={`badge badge-sm badge-outline ${status.badge}`}>{status.label}</span>
                      {row.status === 'approved' && row.expires_at && (
                        <div className="text-2xs text-gray-500 mt-1">Until {formatWhen(row.expires_at)}</div>
                      )}
                    </td>
                    <td>
                      {row.reviewed_by ? (
                        <>
                          <div>{row.reviewed_by}</div>
                          {row.review_note && <div className="text-2xs text-gray-500 max-w-64">“{row.review_note}”</div>}
                        </>
                      ) : '-'}
                    </td>
                    <td className="text-end">{actionsFor(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

export { ExportRequests };
